import { useEffect, useMemo, useRef, useState } from "react";
import { ClipboardCheck, Users, BookOpen, Palmtree, ExternalLink, Sparkles, Mail, Cross, CalendarDays, MapPin, CalendarPlus, Loader2, BadgeCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import RelatedBlogPosts from "@/components/RelatedBlogPosts";
import RegionFilterBar from "@/components/RegionFilterBar";
import SuggestResourceDialog from "@/components/SuggestResourceDialog";
import { filterByRegion, regionLabel } from "@/lib/regions";
import { useAuth } from "@/contexts/AuthContext";

const STYLE_COLORS = {
  visual: "bg-red-50 text-red-700 ring-red-200",
  auditory: "bg-blue-50 text-blue-700 ring-blue-200",
  kinesthetic: "bg-violet-50 text-violet-700 ring-violet-200",
  reading: "bg-amber-50 text-amber-700 ring-amber-200",
};

export default function Resources() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [christianOnly, setChristianOnly] = useState(false);
  const [adding, setAdding] = useState(null); // event.id currently being added to calendar
  const [activeTab, setActiveTab] = useState("testing");
  const [region, setRegion] = useState(user?.region || "all");
  const initialSyncDone = useRef(!!user?.region);

  // Sync region to the user's saved default only on the first time `user.region` becomes known.
  // (Prevents ping-pong overrides after the user manually toggles a pill.)
  useEffect(() => {
    if (!initialSyncDone.current && user?.region) {
      setRegion(user.region);
      initialSyncDone.current = true;
    }
  }, [user?.region]);

  const addEventToCalendar = async (e) => {
    if (!e?.next_date) {
      toast.error("This event doesn't have a fixed date yet — check the organizer's page for exact dates.");
      return;
    }
    setAdding(e.id);
    try {
      await api.post("/lessons", {
        title: e.name,
        date: e.next_date,
        time: "09:00",
        duration_minutes: 240,
        entry_type: "field_trip",
        notes: `${e.description}\n\nOrganizer: ${e.org}\nLocation: ${e.location}\n${e.url}`,
      });
      toast.success(`${e.name} is on your calendar (${e.next_date})`, {
        duration: 8000,
        action: {
          label: "View schedule",
          onClick: () => { window.location.href = `/dashboard/schedule?d=${e.next_date}`; },
        },
      });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setAdding(null);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/resources/all");
        setData(data);
      } catch {}
    })();
  }, []);

  const filteredCurriculum = useMemo(() => {
    if (!data) return [];
    return christianOnly ? data.curriculum.filter((c) => c.christian) : data.curriculum;
  }, [data, christianOnly]);

  // Region filtering — applied to region-tagged tabs only.
  const filteredTesting = useMemo(
    () => (data ? filterByRegion(data.testing?.sites || [], region) : []),
    [data, region],
  );
  const filteredEvents = useMemo(
    () => (data ? filterByRegion(data.events || [], region) : []),
    [data, region],
  );

  if (!data) {
    return (
      <div className="text-stone-500" data-testid="resources-loading">
        Loading resources…
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="resources-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-600">Directory</div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-semibold tracking-tight">
            Homeschool resources hub
          </h1>
          <p className="mt-2 text-stone-500">
            Testing centers, tutor partners, curriculum, co-ops, and Florida scholarships — all curated for homeschool families.
          </p>
        </div>
        <SuggestResourceDialog />
      </div>

      <RegionFilterBar value={region} onChange={setRegion} />
      {region !== "all" && (activeTab === "testing" || activeTab === "events") && (
        <div className="text-xs text-stone-500 -mt-4" data-testid="region-filter-note">
          Showing {activeTab === "testing" ? "testing centers" : "events"} in <strong className="text-stone-700">{regionLabel(region)}</strong> plus statewide items.
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="rounded-full bg-white border border-stone-200 p-1.5 flex flex-wrap h-auto" data-testid="resources-tabs">
          <TabsTrigger value="testing" className="rounded-full data-[state=active]:bg-amber-500 data-[state=active]:text-white" data-testid="tab-testing">
            <ClipboardCheck className="w-3.5 h-3.5 mr-2" /> WRAT-5 Testing
          </TabsTrigger>
          <TabsTrigger value="tutors" className="rounded-full data-[state=active]:bg-amber-500 data-[state=active]:text-white" data-testid="tab-tutors">
            <Users className="w-3.5 h-3.5 mr-2" /> Tutors
          </TabsTrigger>
          <TabsTrigger value="curriculum" className="rounded-full data-[state=active]:bg-amber-500 data-[state=active]:text-white" data-testid="tab-curriculum">
            <BookOpen className="w-3.5 h-3.5 mr-2" /> Curriculum
          </TabsTrigger>
          <TabsTrigger value="coops" className="rounded-full data-[state=active]:bg-amber-500 data-[state=active]:text-white" data-testid="tab-coops">
            <Sparkles className="w-3.5 h-3.5 mr-2" /> Co-ops
          </TabsTrigger>
          <TabsTrigger value="scholarships" className="rounded-full data-[state=active]:bg-amber-500 data-[state=active]:text-white" data-testid="tab-scholarships">
            <Palmtree className="w-3.5 h-3.5 mr-2" /> Florida Scholarships
          </TabsTrigger>
          <TabsTrigger value="events" className="rounded-full data-[state=active]:bg-amber-500 data-[state=active]:text-white" data-testid="tab-events">
            <CalendarDays className="w-3.5 h-3.5 mr-2" /> FL Events
          </TabsTrigger>
        </TabsList>

        {/* WRAT-5 Testing */}
        <TabsContent value="testing" className="mt-6">
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 text-sm text-emerald-900 mb-6" data-testid="wrat-callout">
            <strong>What is WRAT-5?</strong> The Wide Range Achievement Test 5th ed. is a nationally-normed standardized test that Florida homeschool families can use to satisfy the annual evaluation requirement (Statute 1002.41).
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {filteredTesting.length === 0 ? (
              <div className="md:col-span-2 rounded-2xl border border-dashed border-stone-300 bg-white p-8 text-center text-stone-500" data-testid="testing-empty">
                No testing centers listed in {regionLabel(region)} yet. Try <button onClick={() => setRegion("all")} className="underline text-amber-700 hover:text-amber-900">All Florida</button>.
              </div>
            ) : filteredTesting.map((s) => (
              <a key={s.id} href={s.url} target="_blank" rel="noreferrer" className="block bg-white border border-stone-200 rounded-2xl p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all" data-testid={`testing-${s.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] font-semibold text-emerald-600">{s.city}</div>
                    <h3 className="mt-1 font-display text-lg font-semibold leading-snug">{s.name}</h3>
                  </div>
                  <ExternalLink className="w-4 h-4 text-stone-400 shrink-0" />
                </div>
                <div className="mt-3 text-sm text-stone-600">{s.notes}</div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {s.tags.map((t) => (
                    <Badge key={t} variant="secondary" className="rounded-full bg-stone-100 text-stone-700 font-medium text-xs">
                      {t}
                    </Badge>
                  ))}
                </div>
                <div className="mt-3 text-xs text-stone-500">📞 {s.phone}</div>
              </a>
            ))}
          </div>
        </TabsContent>

        {/* Tutors */}
        <TabsContent value="tutors" className="mt-6">
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 text-sm text-amber-900 mb-6">
            <strong>Marketing partnership:</strong> We highlight tutor groups that serve Florida homeschool families. Are you a tutor? See the last card to apply.
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {data.tutors.map((t) => (
              <a key={t.id} href={t.url || "#"} target={t.url && !t.url.startsWith("mailto") ? "_blank" : "_self"} rel="noreferrer" onClick={(e) => { if (!t.url) e.preventDefault(); }} className={`block border rounded-2xl p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all ${t.cta ? "bg-gradient-to-br from-amber-500 to-orange-500 text-white border-transparent grain" : "bg-white border-stone-200"}`} data-testid={`tutor-${t.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    {t.partner && !t.cta && (
                      <div className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] mb-2">
                        Partner
                      </div>
                    )}
                    {t.community && (
                      <div className="inline-flex items-center gap-1 rounded-full bg-violet-100 text-violet-700 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] mb-2" data-testid={`community-badge-${t.id}`}>
                        <Sparkles className="w-2.5 h-2.5" /> Community
                      </div>
                    )}
                    {t.verified && (
                      <div className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] mb-2 ml-1" data-testid={`verified-badge-${t.id}`}>
                        <BadgeCheck className="w-2.5 h-2.5" /> Verified
                      </div>
                    )}
                    <h3 className={`font-display text-lg font-semibold leading-snug ${t.cta ? "text-white" : "text-stone-900"}`}>{t.name}</h3>
                    <div className={`mt-1 text-sm ${t.cta ? "text-white/90" : "text-stone-500"}`}>{t.focus}</div>
                  </div>
                  {t.cta ? <Mail className="w-4 h-4 text-white shrink-0" /> : (t.url ? <ExternalLink className="w-4 h-4 text-stone-400 shrink-0" /> : null)}
                </div>
                <div className={`mt-3 text-sm font-medium ${t.cta ? "text-white" : "text-amber-700"}`}>{t.pricing}</div>
                <ul className={`mt-3 space-y-1 text-sm ${t.cta ? "text-white/90" : "text-stone-600"}`}>
                  {t.highlights.map((h) => (
                    <li key={h}>· {h}</li>
                  ))}
                </ul>
                {t.community && (t.teaching_philosophy || t.florida_expertise) && (
                  <div className="mt-4 rounded-xl bg-amber-50/60 border border-amber-100 p-3 space-y-2 text-sm">
                    {t.teaching_philosophy && (
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-amber-700">Teaching philosophy</div>
                        <p className="mt-0.5 text-stone-700">{t.teaching_philosophy}</p>
                      </div>
                    )}
                    {t.florida_expertise && (
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-amber-700">Florida expertise</div>
                        <p className="mt-0.5 text-stone-700">{t.florida_expertise}</p>
                      </div>
                    )}
                  </div>
                )}
                {t.community && t.submitter && (
                  <div className="mt-3 text-[11px] text-stone-400 italic">Suggested by {t.submitter}</div>
                )}
              </a>
            ))}
          </div>
        </TabsContent>

        {/* Curriculum */}
        <TabsContent value="curriculum" className="mt-6">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div className="text-sm text-stone-500">
              {filteredCurriculum.length} option{filteredCurriculum.length === 1 ? "" : "s"}
              {christianOnly && " · filtered to Christian curricula"}
            </div>
            <button
              onClick={() => setChristianOnly((v) => !v)}
              className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-semibold border transition-colors ${
                christianOnly
                  ? "bg-indigo-500 text-white border-indigo-500"
                  : "bg-white text-stone-700 border-stone-200 hover:border-indigo-300"
              }`}
              data-testid="curriculum-christian-filter"
            >
              <Cross className="w-3.5 h-3.5" />
              {christianOnly ? "Christian only ✓" : "Christian only"}
            </button>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCurriculum.map((c) => (
              <a key={c.id} href={c.url} target="_blank" rel="noreferrer" className="block bg-white border border-stone-200 rounded-2xl p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all" data-testid={`curriculum-${c.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-display text-lg font-semibold leading-snug">{c.name}</h3>
                  <ExternalLink className="w-4 h-4 text-stone-400 shrink-0" />
                </div>
                <div className="mt-2 text-sm text-stone-600">{c.type}</div>
                <div className="mt-4 flex flex-wrap gap-1.5 items-center">
                  {c.christian && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200 px-2.5 py-0.5 text-xs font-semibold" data-testid={`curriculum-christian-badge-${c.id}`}>
                      <Cross className="w-3 h-3" /> Christian
                    </span>
                  )}
                  {c.learning_styles.map((ls) => (
                    <span key={ls} className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ${STYLE_COLORS[ls] || "bg-stone-100 text-stone-700 ring-stone-200"}`}>
                      {ls}
                    </span>
                  ))}
                </div>
              </a>
            ))}
          </div>
        </TabsContent>

        {/* Co-ops */}
        <TabsContent value="coops" className="mt-6">
          <div className="grid md:grid-cols-2 gap-4">
            {filterByRegion(data.coops, region).map((c) => (
              <a key={c.id} href={c.url || "#"} target={c.url ? "_blank" : "_self"} rel="noreferrer" onClick={(e) => { if (!c.url) e.preventDefault(); }} className="block bg-white border border-stone-200 rounded-2xl p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all" data-testid={`coop-${c.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] font-semibold text-amber-600">{c.city}</div>
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                      <h3 className="font-display text-lg font-semibold leading-snug">{c.name}</h3>
                      {c.community && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 text-violet-700 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em]" data-testid={`community-badge-${c.id}`}>
                          <Sparkles className="w-2.5 h-2.5" /> Community
                        </span>
                      )}
                      {c.verified && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em]" data-testid={`verified-badge-${c.id}`}>
                          <BadgeCheck className="w-2.5 h-2.5" /> Verified
                        </span>
                      )}
                    </div>
                  </div>
                  {c.url && <ExternalLink className="w-4 h-4 text-stone-400 shrink-0" />}
                </div>
                <div className="mt-3 text-sm text-stone-600">{c.notes}</div>
                {c.community && (c.teaching_philosophy || c.florida_expertise) && (
                  <div className="mt-3 rounded-xl bg-amber-50/60 border border-amber-100 p-3 space-y-2 text-sm">
                    {c.teaching_philosophy && (
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-amber-700">Teaching philosophy</div>
                        <p className="mt-0.5 text-stone-700">{c.teaching_philosophy}</p>
                      </div>
                    )}
                    {c.florida_expertise && (
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-amber-700">Florida expertise</div>
                        <p className="mt-0.5 text-stone-700">{c.florida_expertise}</p>
                      </div>
                    )}
                  </div>
                )}
                {c.community && c.submitter && (
                  <div className="mt-3 text-[11px] text-stone-400 italic">Suggested by {c.submitter}</div>
                )}
              </a>
            ))}
          </div>
        </TabsContent>

        {/* Florida Scholarships */}
        <TabsContent value="scholarships" className="mt-6">
          <div className="grid md:grid-cols-2 gap-4">
            {data.scholarships.map((s) => (
              <div key={s.id} className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all" data-testid={`scholarship-${s.id}`}>
                <div className="text-xs uppercase tracking-[0.18em] font-semibold text-emerald-600">{s.org}</div>
                <h3 className="mt-2 font-display text-lg font-semibold leading-snug">{s.name}</h3>
                <div className="mt-3 flex items-center gap-2 flex-wrap text-sm">
                  <span className="rounded-full bg-amber-50 text-amber-700 px-3 py-1 font-medium">{s.amount}</span>
                  <span className="text-stone-500">{s.eligibility}</span>
                </div>
                <ul className="mt-4 space-y-1.5 text-sm text-stone-700 list-disc pl-5">
                  {s.highlights.map((h) => <li key={h}>{h}</li>)}
                </ul>
                <a href={s.url} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-amber-600 hover:underline">
                  Learn more <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Florida Homeschool Events — conventions, field-trip days, advocacy */}
        <TabsContent value="events" className="mt-6">
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 text-sm text-amber-900 mb-6" data-testid="events-callout">
            <strong>Florida homeschool calendar.</strong> Conventions, museum homeschool days, and civics field trips — for teachers, students, or the whole family. Running an event? <a href="mailto:events@flhomeschoolhub.com" className="underline">Email us to list it</a>.
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {filteredEvents.length === 0 && (
              <div className="md:col-span-2 rounded-2xl border border-dashed border-stone-300 bg-white p-8 text-center text-stone-500" data-testid="events-empty">
                No events listed in {regionLabel(region)} yet. Try <button onClick={() => setRegion("all")} className="underline text-amber-700 hover:text-amber-900">All Florida</button>.
              </div>
            )}
            {(filteredEvents || []).map((e) => {
              const isMailto = (e.url || "").startsWith("mailto:");
              const isCta = !!e.cta;
              return (
              <div
                key={e.id}
                className={`rounded-2xl p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all border ${isCta ? "bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200" : "bg-white border-stone-200"}`}
                data-testid={`event-${e.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-xs uppercase tracking-[0.18em] font-semibold text-amber-600">{e.org}</div>
                    <a
                      href={e.url}
                      target={isMailto ? undefined : "_blank"}
                      rel="noreferrer"
                      className="mt-1 font-display text-lg font-semibold leading-snug hover:text-amber-600 inline-flex items-start gap-1"
                    >
                      {e.name} <ExternalLink className="w-4 h-4 text-stone-400 shrink-0 mt-1" />
                    </a>
                  </div>
                </div>
                <div className="mt-3 flex items-center flex-wrap gap-3 text-sm text-stone-600">
                  <span className="inline-flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5 text-stone-400" /> {e.when}</span>
                  {e.location && e.location !== "—" && (
                    <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-stone-400" /> {e.location}</span>
                  )}
                </div>
                {e.audience && (
                  <div className="mt-2">
                    <span className="inline-flex items-center rounded-full bg-blue-50 text-blue-700 ring-1 ring-blue-200 px-2 py-0.5 text-xs font-medium">
                      {e.audience}
                    </span>
                  </div>
                )}
                <p className="mt-3 text-sm text-stone-700 leading-relaxed">{e.description}</p>
                {e.tags?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {e.tags.map((t) => (
                      <span key={t} className="rounded-full bg-stone-100 text-stone-600 text-[11px] font-medium px-2 py-0.5">{t}</span>
                    ))}
                  </div>
                )}
                {!isCta && e.next_date && (
                  <div className="mt-4 flex items-center justify-between gap-2 pt-3 border-t border-stone-100">
                    <span className="text-xs text-stone-500">Next: <strong className="text-stone-800">{e.next_date}</strong></span>
                    <button
                      onClick={() => addEventToCalendar(e)}
                      disabled={adding === e.id}
                      className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-1.5 shadow-sm disabled:opacity-60"
                      data-testid={`event-add-${e.id}`}
                    >
                      {adding === e.id ? (<><Loader2 className="w-3.5 h-3.5 animate-spin" /> Adding…</>) : (<><CalendarPlus className="w-3.5 h-3.5" /> Add to my calendar</>)}
                    </button>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      <RelatedBlogPosts tabKey={activeTab} />
    </div>
  );
}
